import { Controller } from '@hotwired/stimulus'
import { DirectUpload } from '@rails/activestorage'
import Dropzone from 'dropzone'
import 'dropzone/dist/dropzone.css'
import { post } from '@rails/request.js'

import {
  getMetaValue,
  findElement,
  removeElement,
  insertAfter
} from '../helpers/helpers'

Dropzone.autoDiscover = false

// Connects to data-controller="dropzone"
export default class extends Controller {
  static targets = ['input']
  static values = {
    url: String,
    startEnabled: { type: Boolean, default: false },
    submitOnUpload: { type: Boolean, default: true },
    pendingListId: { type: String, default: '' }
  }

  connect() {
    this.dropZone = createDropZone(this)
    this.hideFileInput()
    this.bindEvents()

    if (this.startEnabledValue) {
      this.enable()
    } else {
      this.disable()
    }
  }

  disable() {
    // Deshabilita el input de archivo
    this.inputTarget.disabled = true
    // Agrega una clase visual para indicar que está deshabilitado
    this.element.classList.add('dropzone-disabled')
    // Opción: Evitar que Dropzone procese clicks
    this.dropZone.options.clickable = false
  }

  enable() {
    // Habilita el input de archivo
    this.inputTarget.disabled = false
    // Remueve la clase visual
    this.element.classList.remove('dropzone-disabled')
    // Opción: Habilitar que Dropzone procese clicks
    this.dropZone.options.clickable = true
  }

  hideFileInput() {
    this.inputTarget.style.display = "none"
  }

  bindEvents() {
    this.dropZone.on("error", (file, error) => {
      const message = typeof error === "string" && error.length > 0
        ? error
        : "Formato no compatible. Solo se permiten archivos JPG, JPEG, PNG o PDF."

      if (file.previewElement) {
        const errorNodes = file.previewElement.querySelectorAll("[data-dz-errormessage]")
        errorNodes.forEach(node => {
          node.textContent = message
        })
      }
    })

    this.dropZone.on("addedfile", (file) => {
      setTimeout(() => { file.accepted && createDirectUploadController(this, file, this.urlValue).start() }, 500)
    })

    this.dropZone.on("removedfile", (file) => {
      file.controller && removeElement(file.controller.hiddenInput)

      if (file.pendingListItem?.isConnected) {
        file.pendingListItem.remove()
      }

      const list = this.pendingListIdValue ? document.getElementById(this.pendingListIdValue) : null
      const emptyMsg = list?.parentElement?.querySelector('.no-files-msg')

      if (list && list.children.length === 0 && emptyMsg) {
        emptyMsg.classList.remove('d-none')
      }
    })

    this.dropZone.on("canceled", (file) => {
      file.controller && file.controller.xhr.abort()
    })

    this.dropZone.on("processing", (file) => {})

    this.dropZone.on("success", (file) => {
      if (!this.submitOnUploadValue && this.pendingListIdValue) {
        this.appendPendingFile(file)
      }
    })

    this.dropZone.on("queuecomplete", (file) => {
      if (this.submitOnUploadValue) {
        this.dropZone.removeAllFiles()
      }
    })
  }

  appendPendingFile(file) {
    if (file.pendingListItem?.isConnected) return

    const list = document.getElementById(this.pendingListIdValue)
    if (!list) return

    const emptyMsg = list.parentElement?.querySelector('.no-files-msg')
    if (emptyMsg) emptyMsg.classList.add('d-none')

    const li = document.createElement('li')
    li.className = 'd-flex align-items-center justify-content-between mb-1'

    const left = document.createElement('span')
    const nameSpan = document.createElement('span')
    nameSpan.textContent = file.name
    const badge = document.createElement('span')
    badge.className = 'badge badge-secondary ml-1'
    badge.textContent = 'pendiente'
    left.appendChild(nameSpan)
    left.appendChild(badge)

    const removeBtn = document.createElement('button')
    removeBtn.type = 'button'
    removeBtn.className = 'btn btn-sm btn-link text-danger p-0 ml-2'
    removeBtn.innerHTML = '&times;'
    removeBtn.title = 'Quitar archivo'
    removeBtn.addEventListener('click', () => {
      this.dropZone.removeFile(file)
      li.remove()
      if (list.children.length === 0 && emptyMsg) {
        emptyMsg.classList.remove('d-none')
      }
    })

    li.appendChild(left)
    li.appendChild(removeBtn)
    list.appendChild(li)
    file.pendingListItem = li
  }

  get headers() { return { "X-CSRF-Token": getMetaValue("csrf-token") } }

  get url() { return this.inputTarget.getAttribute("data-direct-upload-url") }

  get maxFiles() { return this.data.get("maxFiles") || 1 }

  get maxFileSize() { return this.data.get("maxFileSize") || 256 }

  get acceptedFiles() { return this.data.get("acceptedFiles") || 'image/png,image/jpg,image/jpeg,application/pdf' }

  get addRemoveLinks() { return this.data.get("addRemoveLinks") || true }

  get form() { return this.element.closest("form") }

  get submitButton() { return findElement(this.form, "input[type=submit], button[type=submit]") }
}

class DirectUploadController {
  constructor(source, file, url) {
    this.directUpload = createDirectUpload(file, source.url, this)
    this.source = source
    this.file = file
    this.url = url
  }

  start() {
    this.file.controller = this
    this.hiddenInput = this.createHiddenInput()
    this.directUpload.create((error, attributes) => {
      if (error) {
        removeElement(this.hiddenInput)
        this.emitDropzoneError(error)
      } else {
        this.hiddenInput.value = attributes.signed_id

        if (this.source.submitOnUploadValue && this.url) {
          const tipoDocumento = document.getElementById("tipo_documento_select")?.value
          const subTipoDocumento = document.getElementById("sub_tipo_documento_select")?.value
          const fecha = document.getElementById("archivo_fecha")?.value
          const hora = document.getElementById("archivo_hora")?.value

          post(this.url, {
            responseKind: 'turbo-stream',
            body: {
              archivos: [attributes.signed_id],
              tipo_documento: tipoDocumento,
              sub_tipo_documento: subTipoDocumento,
              fecha: fecha,
              hora: hora
            }
          })
        }

        this.emitDropzoneSuccess()
      }
    })
  }

  // Private
  createHiddenInput() {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = this.source.inputTarget.name
    insertAfter(input, this.source.inputTarget)
    return input
  }

  directUploadWillStoreFileWithXHR(xhr) {
    this.bindProgressEvent(xhr)
    this.emitDropzoneUploading()
  }

  bindProgressEvent(xhr) {
    this.xhr = xhr
    this.xhr.upload.addEventListener("progress", event => this.uploadRequestDidProgress(event))
  }

  uploadRequestDidProgress(event) {
    const element = this.source.element
    const progress = event.loaded / event.total * 100
    findElement(this.file.previewTemplate, ".dz-upload").style.width = `${progress}%`
  }

  emitDropzoneUploading() {
    this.file.status = Dropzone.UPLOADING
    this.source.dropZone.emit("processing", this.file)
  }

  emitDropzoneError(error) {
    removeElement(this.hiddenInput)
    this.file.status = Dropzone.ERROR
    this.source.dropZone.emit("error", this.file, error)
    this.source.dropZone.emit("complete", this.file)
  }

  emitDropzoneSuccess() {
    this.file.status = Dropzone.SUCCESS
    this.source.dropZone.emit("success", this.file)
    this.source.dropZone.emit("complete", this.file)
  }
}

function createDirectUploadController(source, file, url) {
  return new DirectUploadController(source, file, url)
}

function createDirectUpload(file, url, controller) {
  return new DirectUpload(file, url, controller)
}

function createDropZone(controller) {
  return new Dropzone(controller.element, {
    url: controller.url,
    headers: controller.headers,
    maxFilesize: controller.maxFileSize,
    acceptedFiles: controller.acceptedFiles,
    dictInvalidFileType: 'Formato no compatible. Solo se permiten archivos JPG, JPEG, PNG o PDF.',
    addRemoveLinks: false,
    uploadMultiple: true,
    autoQueue: false
  })
}
